// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceLimitMeasure } from '@shared/utils/Instruments/PriceLimitMeasure';
import { Resources } from '@shared/localizations/Resources';
export class Limits {
    constructor(instrument) {
        this.hightLimit = NaN;
        this.lowLimit = NaN;
        this.priceLimitMeasure = PriceLimitMeasure.DISABLE;
        this.isHighLimitFrontEndValidationEnabled = null;
        this.isLowLimitFrontEndValidationEnabled = null;
        this.instrument = instrument;
    }
    get HightLimit() {
        return this.hightLimit;
    }
    set HightLimit(value) {
        this.hightLimit = value;
    }
    get LowLimit() {
        return this.lowLimit;
    }
    set LowLimit(value) {
        this.lowLimit = value;
    }
    get PriceLimitMeasure() {
        return this.priceLimitMeasure;
    }
    set PriceLimitMeasure(value) {
        this.priceLimitMeasure = value;
    }
    get IsHighLimitFrontEndValidationEnabled() {
        return this.isHighLimitFrontEndValidationEnabled;
    }
    set IsHighLimitFrontEndValidationEnabled(value) {
        this.isHighLimitFrontEndValidationEnabled = value;
    }
    get IsLowLimitFrontEndValidationEnabled() {
        return this.isLowLimitFrontEndValidationEnabled;
    }
    set IsLowLimitFrontEndValidationEnabled(value) {
        this.isLowLimitFrontEndValidationEnabled = value;
    }
    IsFrontEndValidation() {
        return this.PriceLimitMeasure === PriceLimitMeasure.FRONT_END_VALIDATION;
    }
    IsHighLimitHide() {
        return this.hightLimit === 0 || this.hightLimit === Limits.LIMIT_NONE;
    }
    IsLowLimitHide() {
        return this.lowLimit === 0 || this.lowLimit === Limits.LIMIT_NONE;
    }
    // function from Java developers for High/Low limit prices
    // prevClose - Базовая цена
    // limit - значение
    getLimitedPrice(type, prevClose, limit) {
        let res = 0;
        if (type === Limits.LimitType.Low) // для Low отнимаем
         {
            limit *= -1;
        }
        switch (this.priceLimitMeasure) {
            case PriceLimitMeasure.DISABLE:
            case PriceLimitMeasure.PERCENTAGE_PREV_CLOSE_PRICE:
            case PriceLimitMeasure.PERCENTAGE_CHANGE_FROM_REFERENCE_PRICE:
                res = prevClose * (1 + limit / 100);
                break;
            case PriceLimitMeasure.OFFSET_SETTL_PRICE:
            case PriceLimitMeasure.OFFSET_PREV_CLOSE_PRICE:
            default:
                res = this.instrument ? this.instrument.calculatePrice(prevClose, limit) : 0;
                break;
        }
        return res > 0 ? res : 0;
    }
    GetLimitPriceByMeasure(type, limitPrice) {
        const instrument = this.instrument;
        switch (this.priceLimitMeasure) {
            case PriceLimitMeasure.PERCENTAGE_SETTL_PRICE:
            case PriceLimitMeasure.OFFSET_SETTL_PRICE: {
                // #93339
                return !isNaN(limitPrice)
                    ? this.getLimitedPrice(type, instrument.PrevSettlementPrice, limitPrice)
                    : null;
            }
            case PriceLimitMeasure.EXCHANGE_LIMITS:
                return limitPrice;
            case PriceLimitMeasure.PERCENTAGE_PREV_CLOSE_PRICE:
            case PriceLimitMeasure.OFFSET_PREV_CLOSE_PRICE: {
                const needUseOpen = isNaN(instrument.Close);
                if (needUseOpen && isNaN(instrument.Open)) {
                    return null;
                }
                const curPrice = needUseOpen ? instrument.Open : instrument.Close;
                return this.getLimitedPrice(type, curPrice, limitPrice);
            }
            case PriceLimitMeasure.PERCENTAGE_CHANGE_FROM_REFERENCE_PRICE: {
                const referencePrice = instrument.Level1.getReferencePrice;
                if (isNaN(referencePrice)) {
                    return null;
                }
                const limRef = this.getLimitedPrice(type, referencePrice, limitPrice);
                // #71795 - делаю округление здесь, чтобы проще покрыть юниттестами
                return this.RoundLimit(type, limRef);
            }
            case PriceLimitMeasure.FRONT_END_VALIDATION:
                return !isNaN(limitPrice) ? limitPrice : null;
            default:
                return null;
        }
    }
    GetHighLimitPriceByMeasure() {
        return this.GetLimitPriceByMeasure(Limits.LimitType.High, this.hightLimit);
    }
    GetLowLimitPriceByMeasure() {
        return this.GetLimitPriceByMeasure(Limits.LimitType.Low, this.lowLimit);
    }
    NoneString(sett) {
        return Resources.getResource('InstrumentDetailsPanel.None');
    }
    HighLimitPriceByMeasureString(sett) {
        const highLimitByMeasure = this.GetHighLimitPriceByMeasure();
        return highLimitByMeasure == null || isNaN(highLimitByMeasure)
            ? this.NoneString(sett)
            : this.instrument.formatPrice(highLimitByMeasure);
    }
    LowLimitPriceByMeasureString(sett) {
        const lowLimitByMeasure = this.GetLowLimitPriceByMeasure();
        return lowLimitByMeasure == null || isNaN(lowLimitByMeasure)
            ? this.NoneString(sett)
            : this.instrument.formatPrice(lowLimitByMeasure);
    }
    RoundLimit(type, limRef) {
        const instrument = this.instrument;
        let roundedLimRef = instrument.roundPrice(limRef);
        if (type === Limits.LimitType.Low) {
            if (limRef > roundedLimRef) {
                roundedLimRef = instrument.calculatePrice(roundedLimRef, 1);
            }
            return roundedLimRef;
        }
        else if (type === Limits.LimitType.High) {
            if (limRef < roundedLimRef) {
                roundedLimRef = instrument.calculatePrice(roundedLimRef, -1);
            }
            return roundedLimRef;
        }
        return limRef;
    }
    GetHighLimitFromPriceLimitsMessage() {
        // #111427
        return this.HightLimit;
    }
    GetLowLimitFromPriceLimitsMessage() {
        // #111427
        return this.LowLimit;
    }
    ValidatePriceOnFrontEnd(price) {
        // #111427
        if (this.IsHighLimitFrontEndValidationEnabled && !isNaN(this.HightLimit) && price > this.HightLimit) {
            return false;
        }
        if (this.IsLowLimitFrontEndValidationEnabled && !isNaN(this.LowLimit) && price < this.LowLimit) {
            return false;
        }
        return true;
    }
}
Limits.LIMIT_NONE = -1;
Limits.LimitType = { High: 0, Low: 1 };
